import React, { useEffect, useState, useCallback } from 'react';
import {
  Landmark, Edit2, Check, X, Power, PowerOff, RefreshCw,
  Hash, SortAsc, Globe, FileText, Eye, EyeOff
} from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';
import { useAuth } from '../../contexts/AuthContext';

// Changing these needs a FinOps administrator (bank_account_routes.SENSITIVE_FIELDS).
const ADMIN_ONLY_FIELDS = new Set(['account_number', 'sort_code']);

export default function BankAccountsList() {
  const { isAdmin } = useAuth();
  const [accounts, setAccounts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [editData, setEditData]       = useState({});
  const [saving, setSaving]           = useState(false);
  const [toggling, setToggling]       = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/bank-accounts?include_inactive=${showInactive}`)
      .then(r => setAccounts(r.data || []))
      .catch(() => toast.error('Failed to load bank accounts'))
      .finally(() => setLoading(false));
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (a) => {
    setEditingId(a.bank_account_id);
    setEditData({
      bank_account_name: a.bank_account_name,
      account_number:    a.account_number,
      sort_code:         a.sort_code,
      bank_name:         a.bank_name,
      currency_code:     a.currency_code,
      notes:             a.notes,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const res = await api.put(`/api/bank-accounts/${id}`, editData);
      setAccounts(prev => prev.map(a => a.bank_account_id === id ? res.data : a));
      setEditingId(null);
      toast.success('Bank account updated');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update bank account');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id) => {
    setToggling(id);
    try {
      const res = await api.post(`/api/bank-accounts/${id}/toggle-active`);
      const updated = res.data;
      if (!showInactive && !updated.is_active) {
        setAccounts(prev => prev.filter(a => a.bank_account_id !== id));
      } else {
        setAccounts(prev => prev.map(a => a.bank_account_id === id ? updated : a));
      }
      toast.success(updated.is_active ? 'Account activated' : 'Account deactivated');
    } catch {
      toast.error('Failed to update account status');
    } finally {
      setToggling(null);
    }
  };

  const EditField = ({ field, label, placeholder }) => {
    // The server refuses these for anyone else; say so rather than let a
    // save fail.
    const locked = ADMIN_ONLY_FIELDS.has(field) && !isAdmin();
    return (
      <div>
        <div className="text-[10px] text-slate-400 mb-0.5">{label}</div>
        <input
          data-testid={`edit-${field}`}
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500"
          value={editData[field] || ''}
          placeholder={placeholder}
          disabled={locked}
          title={locked ? 'Only a FinOps administrator can change this' : undefined}
          onChange={e => setEditData(d => ({ ...d, [field]: e.target.value }))}
        />
      </div>
    );
  };

  const active   = accounts.filter(a => a.is_active);
  const inactive = accounts.filter(a => !a.is_active);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Landmark size={18} className="text-indigo-700" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Bank Accounts</h1>
            <p className="text-xs text-slate-500">
              {active.length} active · {showInactive ? inactive.length + ' inactive' : 'deactivated accounts hidden from dashboard'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="show-inactive-toggle"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Show inactive accounts
          </label>
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="mb-4 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 text-xs text-blue-700 flex items-center gap-2">
        <Eye size={13} />
        <span>Deactivated accounts are hidden from the dashboard and reports. All transaction history is preserved.</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Loading…</div>
      ) : accounts.length === 0 ? (
        <div className="text-center text-slate-400 py-16 text-sm">No bank accounts found</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 w-8"></th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600">Account Name</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600">Bank</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600">Account Number</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600">Sort / Routing</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600">Currency</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600">Status</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {accounts.map(a => {
                const isEditing = editingId === a.bank_account_id;
                return (
                  <tr
                    key={a.bank_account_id}
                    data-testid={`bank-row-${a.bank_account_id}`}
                    className={`transition-colors ${a.is_active ? 'hover:bg-slate-50' : 'bg-slate-50 opacity-70'}`}
                  >
                    {/* Status indicator */}
                    <td className="px-4 py-3">
                      <div className={`w-2 h-2 rounded-full ${a.is_active ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                    </td>

                    {/* Account Name */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <EditField field="bank_account_name" label="Account Name" placeholder="e.g. Main Business Account" />
                      ) : (
                        <div>
                          <div className="font-medium text-slate-800 text-xs">{a.bank_account_name}</div>
                          {a.notes && <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[200px]">{a.notes}</div>}
                        </div>
                      )}
                    </td>

                    {/* Bank Name */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <EditField field="bank_name" label="Bank / Institution" placeholder="e.g. Chase, HSBC" />
                      ) : (
                        <span className="text-xs text-slate-600">{a.bank_name || <span className="text-slate-300">—</span>}</span>
                      )}
                    </td>

                    {/* Account Number */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <EditField field="account_number" label="Account Number" placeholder="●●●● 1234" />
                      ) : (
                        <span className="text-xs font-mono text-slate-600">
                          {a.account_number || <span className="text-slate-300">—</span>}
                        </span>
                      )}
                    </td>

                    {/* Sort Code */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <EditField field="sort_code" label="Sort / Routing" placeholder="20-00-00" />
                      ) : (
                        <span className="text-xs font-mono text-slate-600">
                          {a.sort_code || <span className="text-slate-300">—</span>}
                        </span>
                      )}
                    </td>

                    {/* Currency */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <EditField field="currency_code" label="Currency" placeholder="USD" />
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono">
                          {a.currency_code || 'USD'}
                        </span>
                      )}
                    </td>

                    {/* Status Badge */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        a.is_active
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-100 text-slate-500 border border-slate-200'
                      }`}>
                        {a.is_active ? <Eye size={9} /> : <EyeOff size={9} />}
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid={`save-bank-${a.bank_account_id}`}
                            onClick={() => saveEdit(a.bank_account_id)}
                            disabled={saving}
                            className="p-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <Check size={12} />
                          </button>
                          <button onClick={cancelEdit} className="p-1.5 border border-slate-200 rounded hover:bg-slate-50">
                            <X size={12} className="text-slate-500" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid={`edit-bank-${a.bank_account_id}`}
                            onClick={() => startEdit(a)}
                            className="p-1.5 border border-slate-200 rounded hover:bg-slate-50 text-slate-500"
                            title="Edit details"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            data-testid={`toggle-bank-${a.bank_account_id}`}
                            onClick={() => toggleActive(a.bank_account_id)}
                            disabled={toggling === a.bank_account_id}
                            className={`p-1.5 border rounded disabled:opacity-50 ${
                              a.is_active
                                ? 'border-red-200 text-red-500 hover:bg-red-50'
                                : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                            }`}
                            title={a.is_active ? 'Deactivate account' : 'Reactivate account'}
                          >
                            {a.is_active ? <PowerOff size={12} /> : <Power size={12} />}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
